import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import useListTruncator from 'hooks/useListTruncator'
import { rem } from 'polished'
import type { ComponentProps, MouseEventHandler, ReactElement } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import SearchTileRowInclusionItem, { type SearchTileRowInclusion } from './SearchTileRowInclusionItem'
import clsx from 'clsx'

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${rem(160)}, 1fr));
  gap: ${rem(4)};

  &.singleColumn {
    grid-template-columns: 1fr;
  }
`

interface Props {
  amenities: Array<SearchTileRowInclusion>
  amenitiesTitle?: string | ReactElement
  onMoreClick?: () => void
  truncateAfter?: number
  singleColumn?: boolean
  /**
   * Extra hidden items rendered outside this list (e.g. loyalty inclusions)
   * that should be counted in the "+N more" link total.
   */
  additionalHiddenCount?: number
  /**
   * @default medium
   */
  variant?: ComponentProps<typeof SearchTileRowInclusionItem>['variant']
}

function SearchTileStackAmenitiesList({
  amenities,
  amenitiesTitle,
  onMoreClick,
  truncateAfter = 6,
  singleColumn,
  additionalHiddenCount = 0,
  variant = 'medium',
}: Props) {
  const intl = useIntl()
  const [visible, hidden] = useListTruncator(truncateAfter, amenities)

  const handleMoreClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.preventDefault()
    onMoreClick?.()
  }, [onMoreClick])

  if (!visible.length) return null

  return <Group direction="vertical" gap={4}>
    {!!amenitiesTitle && <BodyText variant="medium" weight="bold">{amenitiesTitle}</BodyText>}
    <Container className={clsx({ singleColumn })}>
      {visible.map((amenity) => <SearchTileRowInclusionItem
        key={amenity.id}
        inclusion={amenity}
        lineClamp={1}
        variant={variant}
      />)}
    </Container>
    {((!!hidden.length || !!additionalHiddenCount) && onMoreClick) && <TextLink size="medium" onClick={handleMoreClick}>{intl.formatMessage({
      id: 'zAzd5T_wazkA5qZCVrbt',
      defaultMessage: '+{count} more',
    }, { count: hidden.length + additionalHiddenCount })}
    </TextLink>}
  </Group>
}

export default SearchTileStackAmenitiesList
